import isNil from 'lodash/isNil'
import { QUESTION_TYPE_EQUIVALENCES } from '@omnea-app/packages/universal/form/validation/questionTypeEquivalences'
import {
  type DynamicOptionResponseSingle,
  type DynamicOptionSource,
} from '@omnea-app/packages/universal/formBuilder/dynamicOption'
import { FormAnswer, FormElementType, FormQuestion } from '@omnea-app/packages/universal/formBuilder/form'
import { isCustomFieldQuestion } from '@omnea-app/packages/universal/formBuilder/isCustomFieldQuestion'
import { Assert, DistributivePick, PickByPropertyType } from '@omnea-app/packages/universal/utility-types/utilityTypes'

export const baseTemplateQuestionConfigs = {
  rfxSupplierProduct: {
    name: 'rfxSupplierProduct',
    type: 'multipleSupplierProduct',
  },
  auctionSuppliers: {
    name: 'auctionSuppliers',
    type: 'multipleSupplierProduct',
    hideProduct: true,
  },
  product: {
    name: 'product',
    type: 'product',
  },
  supplierPortalAssignees: {
    name: 'supplierPortalAssignees',
    type: 'supplierPortalAssignees',
  },
  supplierDescription: {
    name: 'supplierDescription',
    type: 'text',
    variant: 'longText',
  },
  productDescription: {
    name: 'productDescription',
    type: 'text',
    variant: 'longText',
  },
  productWebsite: {
    name: 'productWebsite',
    type: 'text',
    variant: 'website',
  },
  address: {
    name: 'address',
    type: 'address',
  },
  vatRegNumber: {
    name: 'vatRegNumber',
    type: 'taxNumber',
  },
  website: {
    name: 'website',
    type: 'text',
    variant: 'website',
  },
  legalName: {
    name: 'legalName',
    type: 'text',
  },
  contractStartDate: {
    name: 'contractStartDate',
    type: 'date',
  },
  contractEndDate: {
    name: 'contractEndDate',
    type: 'date',
  },
  targetCompletionDate: {
    name: 'targetCompletionDate',
    type: 'date',
  },
  value: {
    name: 'value',
    type: 'price',
  },
  bankAddress: {
    name: 'bankAddress',
    type: 'address',
    isCountryOptional: true,
    overrideIconType: 'bank',
  },
  bankAccountCurrency: {
    name: 'bankAccountCurrency',
    type: 'dynamic-option',
    dataSource: 'currencies',
    overrideIconType: 'bank',
  },
  category: {
    name: 'category',
    type: 'dynamic-option',
    dataSource: 'department', // These exist as department at run time then are updated to the custom core data source  at runtime.
  },
  market: {
    name: 'market',
    type: 'dynamic-option',
    dataSource: 'department',
  },
  localBankDetails: {
    name: 'localBankDetails',
    type: 'bankAccountDetails',
    isInternational: false,
    overrideIconType: 'bank',
  },
  sortCode: {
    name: 'sortCode',
    type: 'textWithConfirmationEntry',
    overrideIconType: 'bank',
  },
  bankAccountNumber: {
    name: 'bankAccountNumber',
    type: 'textWithConfirmationEntry',
    overrideIconType: 'bank',
  },
  bankCountry: {
    name: 'bankCountry',
    type: 'country',
    overrideIconType: 'bank',
  },
  remoteBankAccountCode: { name: 'remoteBankAccountCode', type: 'text', overrideIconType: 'bank' },
  remoteBankAccountName: { name: 'remoteBankAccountName', type: 'text', overrideIconType: 'bank' },
  bankName: { name: 'bankName', type: 'text', overrideIconType: 'bank' },
  internationalBankDetails: {
    name: 'internationalBankDetails',
    type: 'bankAccountDetails',
    isInternational: true,
    overrideIconType: 'bank',
  },
  swiftCode: {
    name: 'swiftCode',
    type: 'swiftCode',
    overrideIconType: 'bank',
  },
  iban: {
    name: 'iban',
    type: 'iban',
    overrideIconType: 'bank',
  },
  supplierBankAccounts: {
    name: 'supplierBankAccounts',
    type: 'bankAccounts',
  },
  supplierTaxNumbers: {
    name: 'supplierTaxNumbers',
    type: 'taxNumbers',
  },
  supplierExternalReference: {
    name: 'supplierExternalReference',
    type: 'externalReference',
  },
  catalogueBasket: {
    name: 'catalogueBasket',
    type: 'catalogueBasket',
  },
  totalValue: {
    name: 'totalValue',
    type: 'price',
  },
  contractLength: {
    name: 'contractLength',
    type: 'number',
  },
  autoRenewal: {
    name: 'autoRenewal',
    type: 'radioGroup',
  },
  cancellationNotice: {
    name: 'cancellationNotice',
    type: 'number',
  },
  negotiatedSavings: {
    name: 'negotiatedSavings',
    type: 'price',
  },
  baselineSpend: {
    name: 'baselineSpend',
    type: 'price',
    hidePaymentInterval: true,
  },
  buyerLegalEntity: {
    name: 'buyerLegalEntity',
    type: 'dynamic-option',
    dataSource: 'subsidiaries',
  },
  department: {
    name: 'department',
    type: 'dynamic-option',
    dataSource: 'department',
  },
  paymentType: {
    name: 'paymentType',
    type: 'dynamic-option',
    dataSource: 'payment-methods',
  },
  primaryContactEmail: {
    name: 'primaryContactEmail',
    type: 'text',
    variant: 'email',
  },
  syncWithAccountingIntegration: {
    type: 'radioGroup',
    name: 'syncWithAccountingIntegration',
  },
  purchaseOrderRequestor: {
    type: 'assignees',
    name: 'purchaseOrderRequestor',
  },
  isPurchaseAgreement: {
    type: 'radioGroup',
    name: 'isPurchaseAgreement',
  },
  lineItems: {
    name: 'lineItems',
    type: 'lineItems',
  },
  paLineItems: {
    name: 'paLineItems',
    type: 'lineItems',
  },
  primaryContactName: {
    name: 'primaryContactName',
    type: 'text',
  },
  primaryContactFirstName: {
    name: 'primaryContactFirstName',
    type: 'text',
  },
  primaryContactLastName: {
    name: 'primaryContactLastName',
    type: 'text',
  },
  primaryContactJobTitle: {
    name: 'primaryContactJobTitle',
    type: 'text',
  },
  primaryContactPhone: {
    name: 'primaryContactPhone',
    type: 'text',
    variant: 'phoneNumber',
  },
  contacts: {
    name: 'contacts',
    type: 'contacts',
  },
  businessOwner: {
    name: 'businessOwner',
    type: 'user',
  },
  additionalBusinessOwners: {
    name: 'additionalBusinessOwners',
    type: 'assignees',
  },
  procurementOwner: {
    name: 'procurementOwner',
    type: 'user',
  },
  accessOwner: {
    name: 'accessOwner',
    type: 'assignees',
  },
  itOwner: {
    name: 'itOwner',
    type: 'user',
  },
  budgetHolder: {
    name: 'budgetHolder',
    type: 'user',
  },
  manager: {
    name: 'manager',
    type: 'assignees',
  },
  budget: {
    name: 'budget',
    type: 'budget',
  },
  projectOwner: {
    name: 'projectOwner',
    type: 'assignees',
    allowSingleAssigneeOnly: true,
  },
  inBudget: {
    name: 'inBudget',
    type: 'radioGroup',
  },
  note: {
    name: 'note',
    type: 'text',
    variant: 'longText',
  },
  paymentTerms: {
    name: 'paymentTerms',
    type: 'dynamic-option',
    dataSource: 'payment-terms',
  },
  pricingUpload: {
    name: 'pricingUpload',
    type: 'files',
  },
  // Renewal creation fields -->
  renewalAssessmentNotice: {
    name: 'renewalAssessmentNotice',
    type: 'number',
    description: 'How many days before the cancellation date should the renewal assessment be sent?',
  },
  renewalAutoStartWorkflow: {
    name: 'renewalAutoStartWorkflow',
    type: 'radioGroup',
  },
  renewalInitialWorkflowAssignee: {
    name: 'renewalInitialWorkflowAssignee',
    type: 'user',
  },
  renewalWorkflow: {
    name: 'renewalWorkflow',
    type: 'workflow',
  },
  renewalTriggerDate: {
    name: 'renewalTriggerDate',
    type: 'date',
    disablePast: true,
  },
  productServiceName: {
    name: 'productServiceName',
    type: 'text',
  },
  // Agreement fields -->
  contractPeriod: {
    name: 'contractPeriod',
    type: 'contractPeriod',
  },
  agreement: {
    name: 'agreement',
    type: 'agreement',
  },
  purchaseOrder: {
    name: 'purchaseOrder',
    type: 'purchase-order',
  },
  parentAgreement: {
    name: 'parentAgreement',
    type: 'agreement',
    agreementSelectionMode: 'parents-only',
    isRequired: false,
  },
  childAgreement: {
    name: 'childAgreement',
    type: 'agreement',
    agreementSelectionMode: 'children-only',
    isRequired: false,
  },
  parentSupplier: {
    name: 'parentSupplier',
    type: 'multipleSupplierProduct',
    hideProduct: true,
    isRequired: false,
  },
  childSupplier: {
    name: 'childSupplier',
    type: 'multipleSupplierProduct',
    hideProduct: true,
    isRequired: false,
  },
  agreementPricing: {
    name: 'agreementPricing',
    type: 'pricing',
    metadata: {
      lockedHiddenFields: {
        agreementType: true,
        paymentTerm: true,
      },
    },
  },
  agreementRenewal: {
    name: 'agreementRenewal',
    type: 'boolean',
  },
  deactivateSupplier: {
    type: 'boolean',
    name: 'deactivateSupplier',
  },
  tprmContinuousMonitoring: {
    type: 'boolean',
    name: 'tprmContinuousMonitoring',
  },
  isReseller: {
    type: 'boolean',
    name: 'isReseller',
  },
  isPreferred: {
    type: 'boolean',
    name: 'isPreferred',
  },
  agreementRemoteId: {
    name: 'agreementRemoteId',
    type: 'text',
  },
  agreementDescription: {
    name: 'agreementDescription',
    type: 'text',
    variant: 'longText',
  },
  supplierCategories: {
    name: 'supplierCategories',
    type: 'supplierCategories',
  },
  agreementCancellationDate: {
    name: 'agreementCancellationDate',
    type: 'agreementCancellationDate',
  },
  assessmentsToCancel: {
    name: 'assessmentsToCancel',
    type: 'assessmentsToCancel',
    description: 'You can only cancel assessments for suppliers you have access to. Contact an admin if you need access.',
  },
  productCatalogueAccess: {
    name: 'productCatalogueAccess',
    type: 'productCatalogueAccess',
  },
  // create assessment fields -->
  assessmentRespondent: {
    name: 'assessmentRespondent',
    type: 'supplierPortalAssignees',
  },
  assessmentWorkflowAutoStart: {
    name: 'assessmentWorkflowAutoStart',
    type: 'boolean',
  },
  assessmentWorkflow: {
    name: 'assessmentWorkflow',
    type: 'workflow',
  },
  assessmentFrequency: {
    name: 'assessmentFrequency',
    type: 'dropdown',
  },
  assessmentTargetStartDateType: {
    name: 'assessmentTargetStartDateType',
    type: 'radioGroup',
  },
  assessmentTargetStartDateRelative: {
    name: 'assessmentTargetStartDateRelative',
    type: 'dropdown',
  },
  assessmentTargetStartDateExact: {
    name: 'assessmentTargetStartDateExact',
    type: 'date',
  },
} as const satisfies Record<
  string,
  DistributivePick<
    FormQuestion,
    | 'name'
    | 'type'
    | 'variant'
    | 'dataSource'
    | 'description'
    | 'allowSingleAssigneeOnly'
    | 'isCountryOptional'
    | 'isInternational'
    | 'overrideIconType'
    | 'metadata'
    | 'disablePast'
    | 'hidePaymentInterval'
    | 'agreementSelectionMode'
    | 'hideProduct'
  > & { isRequired?: boolean }
>

export type TemplateQuestionName = keyof typeof baseTemplateQuestionConfigs
export type TemplateQuestionBaseConfig = (typeof baseTemplateQuestionConfigs)[TemplateQuestionName]

export type TemplateQuestionsResponse = {
  [Name in TemplateQuestionName]?: ParseTemplateQuestionAnswer<Extract<TemplateQuestionBaseConfig, { name: Name }>>
}

export const templateQuestionNames = Object.keys(baseTemplateQuestionConfigs) as TemplateQuestionName[]

export const isTemplateQuestionName = (name?: string): name is TemplateQuestionName =>
  !isNil(name) && name in baseTemplateQuestionConfigs

export const supplierHierarchyQuestionNames = ['parentSupplier', 'childSupplier'] as const

export const isSupplierHierarchyQuestionName = (name: string): name is (typeof supplierHierarchyQuestionNames)[number] =>
  supplierHierarchyQuestionNames.some((questionName) => questionName === name)

/** Minimal shape required for template question checks */
type TemplateQuestionInput = { name?: string; customField?: unknown; budgetApprovalGroup?: unknown }

export const isTemplateQuestion = (question: TemplateQuestionInput): boolean => {
  return isTemplateQuestionName(question.name) || isCustomFieldQuestion(question) || isBudgetApprovalGroupQuestion(question)
}

export const isBudgetApprovalGroupQuestion = <T extends TemplateQuestionInput>(
  question: T
): question is T & { budgetApprovalGroup: NonNullable<T['budgetApprovalGroup']> } => {
  return 'budgetApprovalGroup' in question && !!question.budgetApprovalGroup
}

export const isValidTemplateQuestion = (question: FormQuestion): boolean => {
  if (!isTemplateQuestionName(question.name)) return false

  const config = baseTemplateQuestionConfigs[question.name]

  return matchesTemplateConfig(config, question, { allowEquivalentTypes: true })
}

export const isValidTemplateAnswer = (
  name: string,
  answer?: FormAnswer | null
): answer is TemplateQuestionsResponse[TemplateQuestionName] => {
  if (!answer) return false
  if (!isTemplateQuestionName(name)) return false

  const config = baseTemplateQuestionConfigs[name]

  return matchesTemplateConfig(config, answer)
}

export const matchesTemplateConfig = (
  config: TemplateQuestionBaseConfig,
  questionOrAnswer: FormQuestion | FormAnswer,
  { allowEquivalentTypes = false }: { allowEquivalentTypes?: boolean } = {}
): boolean => {
  const equivalentTypes = allowEquivalentTypes ? QUESTION_TYPE_EQUIVALENCES[config.name] : undefined
  const typeMatches = equivalentTypes ? equivalentTypes.has(questionOrAnswer.type) : config.type === questionOrAnswer.type
  if (!typeMatches) return false

  if ('variant' in config) {
    if (!('variant' in questionOrAnswer)) return false
    if (config.variant !== questionOrAnswer.variant) return false
  }

  if ('displayAs' in config) {
    if (!('displayAs' in questionOrAnswer)) return false
    if (config.displayAs !== questionOrAnswer.displayAs) return false
  }

  if ('dataSource' in config) {
    if (!('dataSource' in questionOrAnswer)) return false

    if (config.name === 'category' || config.name === 'market') {
      if (config.dataSource !== questionOrAnswer.dataSource && questionOrAnswer.dataSource !== null) return true
    }

    if (config.dataSource !== questionOrAnswer.dataSource) return false
  }

  return true
}

/** ******************
 *   HELPER TYPES   *
 ********************/
// This type will check if the config matches any answers in a form response payload.
// It will return the answer if it matches, or an error message if it doesn't.
// Some of these checks are already performed by the `satisfies` above, but are also needed here so the narrowing works.

export type ParseTemplateQuestionAnswer<Config> = Config extends {
  type: infer Type
}
  ? Type extends FormElementType
    ? Config extends { dataSource: infer DataSource }
      ? Config extends { type: 'dynamic-option' }
        ? DataSource extends DynamicOptionSource
          ? DynamicOptionResponseSingle & { dataSource: DataSource } // We assume core data template questions will always be single choice
          : "Invalid 'dataSource' property, must be a valid 'DynamicOptionSource'"
        : "If 'dataSource' is defined, 'type' must be 'dynamic-option'. To fix, either update 'type' to be 'dynamic-option' or remove the 'dataSource' property"
      : Config extends { type: 'dynamic-option' }
        ? "Property 'dataSource' is missing for config of type 'dynamic-option'. To fix, either define a 'dataSource' or change 'type' to something else"
        : Extract<FormAnswer, { type: Config['type'] }>
    : "Invalid 'type' property, must be a valid 'FormElementType'"
  : "Property 'type' is missing in the config"

// `ConfigParsingErrors` will contain any error messages if a config doesn't match any answers in the form response payload.
type ConfigParsingErrors = PickByPropertyType<
  {
    [Name in TemplateQuestionName]: ParseTemplateQuestionAnswer<Extract<TemplateQuestionBaseConfig, { name: Name }>>
  },
  string
>

/**
 * In case of a type error, do this:
 * 1. Check which fields are erroring by hovering over `ConfigParsingErrors` (right above this comment).
 * 2. Fix the error in `baseTemplateQuestionConfigs`
 *
 * This is a build-time check to ensure that `baseTemplateQuestionConfigs` is valid.
 * This type is too complex to be used in conjunction with the `satifies` above, hence the need for this check.
 */
type _AssertAllConfigsMatchSomeFormAnswer = Assert<ConfigParsingErrors, Record<string, never>>
//                                                 ↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑ If this is erroring, read the comment above
